/**
 * `resolutionThrows` — the rendezvous between "this resolution's dice were thrown" and "something
 * wants to wait for them."
 *
 * A resolution reaches consumers that cannot hand each other a promise directly. `CombatApp`
 * throws the dice off `encounter.latestResolution`; the `PresentationDirector` compiles the same
 * resolution into board beats through its own hook, declared earlier in the same component and so
 * running its effect FIRST. A third consumer — the COMMS result card — arrives on a different
 * socket entirely, with no ordering guarantee against either. So the wait is looked up by **key**
 * rather than passed down a tree.
 *
 * The registry is deliberately order-agnostic: asking for a key that has not been registered yet
 * returns a promise that is fulfilled when the registration lands. That is what makes the
 * effect-ordering above a non-issue rather than something to reason about at every call site —
 * whichever side runs first, the rendezvous is exact. It is **not** a grace window: nothing here
 * guesses whether a throw "belongs" to a waiter. Only a matching key links them, and the
 * {@link SETTLE_CEILING_MS} ceiling under every wait is a backstop against a registration that
 * never comes, not the mechanism.
 *
 * The contract callers have to hold up: **register exactly once for every resolution observed,
 * including the ones that throw nothing** (register an already-resolved promise). A resolution
 * that is observed but never registered is the one shape that makes a waiter sit out the full
 * ceiling.
 */
import { SETTLE_CEILING_MS } from "./throwSettlement";

/**
 * How many resolutions' entries to keep. Well past any barrage (two shots) or AoE fan-out, and
 * bounded so a four-hour session doesn't accumulate one entry per attack. Evicting an entry only
 * discards a settled rendezvous — a waiter already holds its promise.
 */
const MAX_TRACKED = 16;

type Entry = {
  done: Promise<void>;
  /** Fulfil this entry from the real throw. First registration wins; later ones are ignored. */
  link: (throwDone: Promise<void>) => void;
  linked: boolean;
  /**
   * The registration waits on a person, not only on dice: a chained resolution's held roll
   * (`chained-resolution-sequence` 3.3). A waiter's ceiling does not release it.
   */
  unbounded: boolean;
};

/** Insertion-ordered, so eviction is "oldest first" without a timestamp. */
const entries = new Map<string, Entry>();

function entryFor(key: string): Entry {
  const existing = entries.get(key);
  if (existing !== undefined) return existing;
  let resolve!: () => void;
  const entry: Entry = {
    done: new Promise<void>((r) => {
      resolve = r;
    }),
    linked: false,
    unbounded: false,
    link: (throwDone) => {
      if (entry.linked) return;
      entry.linked = true;
      void throwDone.then(resolve, resolve);
    },
  };
  entries.set(key, entry);
  while (entries.size > MAX_TRACKED) {
    const oldest = entries.keys().next();
    if (oldest.done === true) break;
    entries.delete(oldest.value);
  }
  return entry;
}

/**
 * Publish this resolution's dice throw. Call it for **every** resolution observed — pass an
 * already-resolved promise for one that throws nothing, so a waiter is released at once instead
 * of sitting out the ceiling.
 */
export function registerResolutionThrow(key: string, throwDone: Promise<void>, opts: { unbounded?: boolean } = {}): void {
  const entry = entryFor(key);
  if (!entry.linked && opts.unbounded === true) entry.unbounded = true;
  entry.link(throwDone);
}

/**
 * Wait for this resolution's dice to stop rolling. Resolves immediately once they have; waits for
 * the registration first if it hasn't happened yet; and gives up after `ceilingMs` regardless, so
 * a missing registration costs a late reveal rather than one that never comes.
 */
export function awaitResolutionThrow(key: string, ceilingMs: number = SETTLE_CEILING_MS): Promise<void> {
  const entry = entryFor(key);
  const { done } = entry;
  if (ceilingMs <= 0) return done;
  return new Promise<void>((resolve) => {
    // The ceiling is a backstop against a registration that never comes, so it does not cut short
    // one that waits on a held roll's release (checked when it fires: registration may land late).
    const timer = setTimeout(() => {
      if (!entry.unbounded) resolve();
    }, ceilingMs);
    void done.then(() => {
      clearTimeout(timer);
      resolve();
    });
  });
}

/**
 * How many combat-resolution throwers are mounted. A **counter**, not a boolean, because React
 * StrictMode runs mount effects twice and a remount can overlap an unmount.
 */
let sources = 0;

/**
 * Announce that a combat-resolution dice thrower (COMBAT.app) is mounted; returns the unmark, so
 * it drops straight into a `useEffect`.
 *
 * This exists for one case, and it is a real one rather than a defensive flourish: COMBAT.app is
 * the only thing that throws resolution dice, so while it is closed **no throw is ever coming** —
 * and COMMS.app is still showing that resolution's result card. Without this signal a closed board
 * would make every result card wait out the full ceiling before appearing.
 */
export function markResolutionThrowSource(): () => void {
  sources += 1;
  return () => {
    sources -= 1;
  };
}

/**
 * Is anything mounted that could throw dice for a combat resolution? A waiter reads this to tell
 * "the throw hasn't registered YET" from "no throw is coming at all."
 *
 * Deliberately **not** consulted for ad-hoc `/roll`: that path is thrown by `DiceFeedSubscriber`,
 * which is mounted as a peer of COMMS.app itself, so a `/roll` card can always expect its dice.
 */
export function hasResolutionThrowSource(): boolean {
  return sources > 0;
}

/** Drop every tracked resolution — test isolation only; the module is a process-wide singleton. */
export function resetResolutionThrows(): void {
  entries.clear();
  sources = 0;
}
